import React from 'react'
import cn from 'classnames'
import { computed, Lambda, toJS } from 'mobx'
import {
  GridApi,
  ColumnApi,
  ColDef,
  RowNode,
  GridReadyEvent,
  ValueGetterParams,
  ValueSetterParams,
  CellClassParams,
  BodyScrollEvent,
  SelectionChangedEvent,
  CellEditingStoppedEvent,
} from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { debounce, throttle, isEqual, last, findLast } from 'lodash'

import RecordStore from 'stores/record/RecordStore'
import Record from 'stores/record/Record'
import Field from 'stores/field/Field'
import FieldStore from 'stores/field/FieldStore'
import ActionStore from 'stores/action/ActionStore'
import TabStore from 'stores/tab/TabStore'

import Mousetrap from 'components/mousetrap/Mousetrap'
import TableLoadingOverlay from './TableLoadingOverlay'
import TableEmptyOverlay from './TableEmptyOverlay'
import TableCellHeader from './TableCellHeader'
import TableCellRenderer from './TableCellRenderer'
import TableCellEditor from './TableCellEditor'

import getRecordId from 'utils/getRecordId'
import stringify from 'utils/stringify'

import styles from './table.scss'

interface IProps {
  width: number
  height: number
}

export const TABLE_ROW_HEIGHT = 32

class Table extends React.Component<IProps> {
  table: React.RefObject<HTMLDivElement> = React.createRef()
  gridApi!: GridApi
  columnApi!: ColumnApi

  // Used by the grid to sort efficiently
  recordOrder: Map<string, number> = new Map()

  // Used to dispose observers
  reactionDisposers: Lambda[] = []

  loadGridDataDebounced: () => void
  handleScrollThrottled: (e: BodyScrollEvent) => void

  constructor(props: IProps) {
    super(props)

    this.loadGridDataDebounced = debounce(this.loadGridData, 300, {
      leading: false,
      trailing: true,
    })
    this.handleScrollThrottled = throttle(this.handleScroll, 100, {
      leading: true,
      trailing: true,
    })
  }

  componentWillUnmount() {
    this.disposeReactions()
  }

  public focus = () => {
    this.table.current?.focus()
  }

  public refresh = () => {
    this.loadGridDataDebounced()
  }

  public selectRows = (rowIds: string[] = []) => {
    this.gridApi.deselectAll()

    rowIds.forEach(rId => {
      this.gridApi?.getRowNode(rId)?.setSelected(true)
    })
  }

  public deleteSelectedRows = () => {
    const sessionId = TabStore.activeTab?.sessionId

    if (!sessionId) {
      return
    }

    const selectedRecords = this.gridApi
      .getSelectedNodes()
      .map(n => RecordStore.get(n.id))
      .filter((r): r is Record => !!r)

    selectedRecords.forEach(r =>
      ActionStore.add({
        type: 'delete',
        recordId: r.id,
        sessionId,
        value: {},
      })
    )
  }

  getDimensions = () => this.table.current?.getBoundingClientRect()

  resizeRowForRelation = (node: RowNode, field: Field) => {
    if (!field.typeAsModel) {
      return false
    }

    const parentTableDimensions = this.getDimensions()
    if (!parentTableDimensions) {
      return
    }

    const headerHeight = 64
    const scrollBarHeight = 15 // This is a potential scrollbar, may not be present if the screen is wide enough to fit all columns
    const footerHeight = 36
    const contentEmptyHeight = TABLE_ROW_HEIGHT * 2 // Temporary, till we have a design and know this height
    const contentMaxHeight =
      parentTableDimensions.height -
      TABLE_ROW_HEIGHT *
        4 /* 1x for the parent table header, 1x for the current row, 2x to make sure at least two more rows are visible after this accordion */ -
      headerHeight -
      scrollBarHeight -
      footerHeight

    // Yes, most of these will cancel each other out, but look how easy it is to reason about!
    const maxHeight =
      headerHeight + contentMaxHeight + scrollBarHeight + footerHeight
    const minHeight =
      headerHeight + contentEmptyHeight + scrollBarHeight + footerHeight
    const actualHeight =
      headerHeight +
      TABLE_ROW_HEIGHT * (field.typeAsModel.count || 0) +
      scrollBarHeight +
      footerHeight

    const tableHeight = Math.min(Math.max(minHeight, actualHeight), maxHeight)

    node.setRowHeight(TABLE_ROW_HEIGHT + tableHeight) // Set this row's height to account for this row + the table that will be rendered inside
    this.gridApi.onRowHeightChanged()

    // First, scroll to the row of the cell in question
    this.gridApi.ensureNodeVisible(node, 'top')
  }

  loadGridData = async () => {
    const session = TabStore.activeTab?.session

    if (!session || !session.isScript) {
      return
    }

    if (
      session.script.model.count === 0 &&
      session.script.recordIds.length === 0
    ) {
      this.gridApi.showNoRowsOverlay()
      return
    }

    this.gridApi.showLoadingOverlay()
    await session.script.run()
    this.gridApi.hideOverlay()
  }

  configureGridColumns = () => {
    const session = TabStore.activeTab?.session

    if (!session || !session.isScript) {
      return
    }

    // I *think* ag-grid should do this automatically, but it doesn't, which causes a crash in some ocassions, so we'll do it
    this.gridApi.stopEditing()
    this.gridApi.setColumnDefs([
      {
        colId: 'checkbox',
        headerName: '',
        editable: false,
        resizable: false,
        sortable: false,
        pinned: true,
        suppressMovable: true,
        maxWidth: 32,
        checkboxSelection: true,
        headerCheckboxSelection: true,
      } as ColDef,
      ...session.script.model.fields.map(
        f =>
          ({
            colId: f.id,
            headerName: f.name,
            editable: true,
            resizable: true,
            sortable: true,
            hide: !session.script.fieldIds.includes(f.id),
            valueGetter: (params: ValueGetterParams) =>
              (params.data as Record).value[f.name],
            valueSetter: (params: ValueSetterParams) =>
              this.handleCellValueChanged(params, f),
            comparator: (
              colValueA: any,
              colValueB: any,
              recordA: any,
              recordB: any,
              isInverted: boolean
            ) =>
              (this.recordOrder.get(recordA.id)! -
                this.recordOrder.get(recordB.id)! || 0) * (isInverted ? -1 : 1),
            headerComponent: 'TableCellHeader',
            headerComponentParams: {
              field: f,
              script: session.script,
            },
            cellRenderer: 'TableCellRenderer',
            cellRendererParams: {
              field: f,
              resizeRowForRelation: this.resizeRowForRelation,
            },
            cellClassRules: {
              [styles.tableCell]: () => true,
              [styles.dirty]: ({ data }: CellClassParams) =>
                (data as Record).dirtyFieldNames.includes(f.name),
              [styles.invalid]: ({ data }: CellClassParams) =>
                !!(data as Record).invalidFields.find(i => i.field.id === f.id),
              [styles.empty]: ({ data }: CellClassParams) =>
                (data as Record).value[f.name] === undefined ||
                (data as Record).value[f.name] === null,
            },
            cellEditor: 'TableCellEditor',
            cellEditorParams: {
              field: f,
              sessionId: session.id,
              getTableDimensions: this.getDimensions,
              resizeRowForRelation: this.resizeRowForRelation,
            },
          } as ColDef)
      ),
    ])

    // Scroll to the first column
    this.gridApi.ensureColumnVisible(session.script.model.fieldIds[0])
  }

  configureReactions = () => {
    this.disposeReactions()

    // Watch when the active tab changes, so we can reset the grid's data
    let disposer = computed(() => [
      TabStore.activeTab,
      TabStore.activeTab?.session,
    ]).observe(() => {
      this.configureGridColumns()
      this.loadGridDataDebounced()
    })
    this.reactionDisposers.push(disposer)

    // Watch when the script's fields change, so we can show / hide grid columns
    disposer = computed(() =>
      TabStore.activeTab?.session?.isScript
        ? TabStore.activeTab?.session?.script?.fields
        : []
    ).observe(({ oldValue = [], newValue = [] }) => {
      if (!TabStore.activeTab?.session?.isScript) {
        return
      }

      let fieldsToShow: Field[] = []
      let fieldsToHide: Field[] = []

      const oldFields = new Map()
      oldValue.forEach(f => oldFields.set(f.id, f))

      const newFields = new Map()
      newValue.forEach(f => newFields.set(f.id, f))

      newFields.forEach((f, fId) => {
        if (oldFields.has(fId)) {
          // Field existed before, and still exists
        } else {
          // Field was added
          fieldsToShow.push(f)
        }
        oldFields.delete(fId)
      })

      // Whatever's left in oldFields was deleted
      oldFields.forEach(f => fieldsToHide.push(f))

      fieldsToShow.forEach(f => this.columnApi.setColumnVisible(f.id, true))
      fieldsToHide.forEach(f => this.columnApi.setColumnVisible(f.id, false))
    })
    this.reactionDisposers.push(disposer)

    // Watch when the script's records change, so we can add / remove records
    disposer = computed(() =>
      TabStore.activeTab?.session?.isScript
        ? TabStore.activeTab?.session?.script?.records
        : []
    ).observe(({ oldValue = [], newValue = [] }) => {
      if (!TabStore.activeTab?.session?.isScript) {
        return
      }

      const recordsToAdd: Record[] = []
      const recordsToUpdate: Record[] = []
      const recordsToRemove: Record[] = []

      const oldRecords: Map<string, Record> = new Map()
      oldValue.forEach(r => oldRecords.set(r.id, r))

      const newRecords: Map<string, Record> = new Map()
      newValue.forEach(r => newRecords.set(r.id, r))

      let idx = 0
      this.recordOrder.clear()
      newRecords.forEach(r => {
        this.recordOrder.set(r.id, idx++)
        if (oldRecords.has(r.id) && this.gridApi.getRowNode(r.id)) {
          // Record existed before, and still exists
          recordsToUpdate.push(r)
        } else {
          // Record was added
          recordsToAdd.push(r)
        }
        oldRecords.delete(r.id)
      })

      // Whatever's left in oldRecords was deleted (provided it exists in the grid at all)
      oldRecords.forEach(r => {
        if (this.gridApi.getRowNode(r.id)) {
          recordsToRemove.push(r)
        }
      })

      // If new records contain (at least one) uncommitted rows, scroll the table to the top (new records might also come from other places, like a table refresh)
      // To do this, we find the LAST uncommitted record. This is useful because we'd also like to focus the first cell of this record
      const lastUncommittedRecord = findLast(
        recordsToAdd,
        r => r.isCommitted === false
      )
      if (lastUncommittedRecord) {
        // Presence of `lastUncommittedRecord` also implies that there was at last one uncommitted record.
        this.gridApi.deselectAll()
        this.gridApi.ensureIndexVisible(0)

        // Try to focus the first cell of this record
        this.gridApi.setFocusedCell(
          TabStore.activeTab!.session!.script!.uncommittedRecords.length - 1, // rowIndex = number of uncommitted records
          last(recordsToAdd)!.model.fieldIds[0] // columnKey = ID of first field of this record
        )
      }

      // Setting the sort model to null here is important for performance.
      // If you don't do this, the applyTransaction below will cause the grid to try and sort itself. We do not want that.
      // We will cause the grid to reorder ANYWAY
      this.gridApi.setSortModel(null)
      this.gridApi.applyTransaction({
        add: recordsToAdd,
        update: recordsToUpdate,
        remove: recordsToRemove,
      })

      // Make sure the grid is always sorted using the order of records in the script.
      // The columnId does not matter, since we're no using it to order, so I just set it to something valid.
      this.gridApi.setSortModel([
        {
          colId: TabStore.activeTab?.session?.script.fieldIds[0],
          sort: TabStore.activeTab?.session?.script.sort.order,
        },
      ])
    })
    this.reactionDisposers.push(disposer)
  }

  disposeReactions = () => {
    this.reactionDisposers.forEach(d => d())
    this.reactionDisposers = []
  }

  handleGridReady = async (e: GridReadyEvent) => {
    this.columnApi = e.columnApi
    this.gridApi = e.api

    this.configureReactions()
    this.configureGridColumns()
    this.loadGridDataDebounced()
  }

  handleScroll = async (e: BodyScrollEvent) => {
    if (e.direction === 'horizontal') {
      return
    }

    const script = TabStore.activeTab?.session?.script!

    if (script.recordIds.length >= (script.model.count || 0)) {
      // We're already at the end of the list, no need to try and fetch more
      return
    }

    const tableHeight = this.props.height
    const rowHeight = 32

    // In the following line, we're trying to fetch the next page of records when you're 20 records from the bottom of the list
    if (
      !script.running &&
      e.top + tableHeight >= rowHeight * (script.pagination.take - 20)
    ) {
      script.loadMore()
    }
  }

  handleSelectionChanged = (params: SelectionChangedEvent) => {
    TabStore.activeTab?.session!.selection.table.update({
      selectedRecordIds: params.api.getSelectedRows().map(r => r.id),
    })
  }

  handleCellEditingStopped = (params: CellEditingStoppedEvent) => {
    const focusedCell = this.gridApi.getFocusedCell()
    const editingCell = params

    if (!focusedCell || !editingCell) {
      return
    }

    if (
      focusedCell.rowIndex === editingCell.rowIndex &&
      focusedCell.column.getColId() !== editingCell.column.getColId()
    ) {
      // If these two cells are the same, do nothing
      // This exists so people can exit edit mode when pressing Esc / Enter
      return
    }

    if (focusedCell.rowIndex === editingCell.rowIndex) {
      // If these two cells are in the same row, then do nothing
      // This exists because we do not want to keep opening the relation accordion when you switch to a different column.
      return
    }

    const focusedField = FieldStore.get(focusedCell.column.getColId())
    const editingField = FieldStore.get(params.column.getColId())

    if (editingField?.isRelation && focusedField?.isRelation) {
      // If current cell is a relation, and you clicked on another relation, start editing that other relation
      this.gridApi.startEditingCell({
        rowIndex: focusedCell.rowIndex,
        colKey: focusedCell.column.getColId(),
      })
    }
  }

  handleCellValueChanged = (
    params: ValueSetterParams,
    field: Field
  ): boolean => {
    const {
      oldValue,
      newValue,
      node: { data },
    } = params
    const record: Record = data

    const sessionId = TabStore.activeTab?.sessionId

    if (!sessionId) {
      return false
    }

    if (isEqual(oldValue, newValue)) {
      return false
    }

    if (!TabStore.activeTab?.session?.isScript) {
      return false
    }

    TabStore.activeTab.session.script!.update({ frozen: false })

    // 1. Add an action signifying that this field was changed
    ActionStore.add({
      type: 'update',
      recordId: record.id,
      sessionId,
      value: {
        [field.name]: newValue,
      },
    })

    /**
     * We're also creating a 2-way binding between scalar fields that are part of a relation, and the relation itself.
     * 2a. If you change the relation, the scalar changes as well
     * 2b. If you change the scalar, the relation changes as well
     * So, we're addding additional actions to "implement" this two-way binding
     * (all this does not apply to relation lists)
     *
     * So, assume we are working with this schema:
     *
     * model User {
     *   id    Int
     *   name  String
     *
     *   @@id(fields: [id, name])
     * }
     *
     * model Post {
     *   id      Int  @id
     *
     *   userId    Int
     *   userName  String
     *   user      User  @relation(fields: [userId, userName], references: [id, name])
     * }
     *
     * Also assume we're editing the `Post` model. It has one record:
     * {
     *   id: `Post::0`,
     *   modelId: 'Post',
     *   value: {
     *     id: 0,
     *     userId: 0,
     *     userName: 'user0',
     *     user: { id: 0, name: 'user0' }
     *   }
     * }
     */

    // 2a. If you changed the relation
    if (field.isRelation && !field.isList) {
      // If you changed the `user` to { id: 1, name: 'user1' },
      // we have to add an update action that changes -> { userId: 1, userName: 'user1' }

      if (!field.typeAsModel) return false

      const relationFromFieldNames = field.relationFromFieldNames // = [userId, userName]
      const relationToFieldNames = field.relationToFieldNames // = [id, name]

      // Find the record you connected to
      let connectedRecord: Record | null
      if (newValue === undefined || newValue === null) {
        connectedRecord = null
      } else {
        connectedRecord = RecordStore.get(
          getRecordId(field.typeAsModel.id, newValue)
        )
      }

      ActionStore.add({
        type: 'update',
        sessionId,
        recordId: record.id ?? null,
        value: relationFromFieldNames.reduce((acc, fieldName, idx) => {
          acc[fieldName] =
            connectedRecord?.value[relationToFieldNames[idx]] ?? null
          return acc
        }, {} as any),
      })
    }

    // 2b. If you changed a scalar that is part of a relation
    if (field.isPartOfRelation && field.relationItIsPartOf) {
      // If you changed -> { userId: 1 }
      // we have to add an update action that changes -> { user: { id: 1, name: 'user1' } }

      if (!field.relationItIsPartOf.typeAsModel) return false

      const relationFromFieldNames =
        field.relationItIsPartOf.relationFromFieldNames // = [userId, userName]
      const relationToFieldNames = field.relationItIsPartOf.relationToFieldNames // = [id, name]

      ActionStore.add({
        type: 'update',
        sessionId,
        recordId: record.id,
        value: {
          [field.relationItIsPartOf.name]:
            newValue === undefined || newValue === null
              ? null
              : relationToFieldNames.reduce((acc, fieldName, idx) => {
                  acc[fieldName] = record.value[relationFromFieldNames[idx]]
                  return acc
                }, {} as any),
        },
      })
    }

    return true
  }

  copyToClipboard() {
    const text = stringify(
      this.gridApi.getSelectedRows().map(r => toJS(r.value)),
      0
    )
    // Print to console in case copying fails
    console.log(text)

    if (
      document.queryCommandSupported &&
      document.queryCommandSupported('copy')
    ) {
      var textarea = document.createElement('textarea')
      textarea.textContent = text
      textarea.style.position = 'fixed' // Prevent scrolling to bottom of page in Microsoft Edge.
      document.body.appendChild(textarea)
      textarea.select()
      try {
        return document.execCommand('copy') // Security exception may be thrown by some browsers.
      } catch (ex) {
        console.warn('Copy to clipboard failed.', ex)
        return false
      } finally {
        document.body.removeChild(textarea)
      }
    }
  }

  render() {
    const session = TabStore.activeTab?.session

    return (
      <>
        <div ref={this.table} className={cn(styles.container, 'ag-theme-main')}>
          <AgGridReact
            rowSelection="multiple"
            rowDeselection
            suppressRowClickSelection
            frameworkComponents={{
              TableLoadingOverlay,
              TableEmptyOverlay,
              TableCellHeader,
              TableCellRenderer,
              TableCellEditor,
            }}
            rowClassRules={{
              [styles.firstCommittedRow]: params =>
                session!.script!.uncommittedRecords.length
                  ? params.node.id ===
                    session!.script!.recordIds[
                      session!.script!.uncommittedRecords.length
                    ]
                  : false,
            }}
            loadingOverlayComponent="TableLoadingOverlay"
            noRowsOverlayComponent="TableEmptyOverlay"
            suppressColumnVirtualisation={!!window.Cypress} // Do not virtualize columns when testing, makes for easier assertions
            getRowNodeId={params => (params as Record).id}
            onGridReady={this.handleGridReady}
            onBodyScroll={this.handleScrollThrottled} // We're not using the infinite row model because we want to change row heights after they've been set once
            onSelectionChanged={this.handleSelectionChanged}
            onCellEditingStopped={this.handleCellEditingStopped}
          />
        </div>

        <Mousetrap
          keys="mod+c"
          target={this.table}
          preventDefault={false}
          stopPropagation={false}
          onMatch={e => {
            if (this.gridApi.getSelectedRows().length === 0) {
              return
            }

            e.preventDefault()
            e.stopPropagation()
            this.copyToClipboard()
          }}
        />

        <Mousetrap
          keys="mod+up"
          target={this.table}
          preventDefault={false}
          stopPropagation={false}
          onMatch={e => {
            if (this.gridApi.getEditingCells().length > 0) {
              return
            }

            e.preventDefault()
            e.stopPropagation()
            this.gridApi.setFocusedCell(0, this.gridApi.getFocusedCell().column)
          }}
        />
        <Mousetrap
          keys="mod+down"
          target={this.table}
          preventDefault={false}
          stopPropagation={false}
          onMatch={e => {
            if (this.gridApi.getEditingCells().length > 0) {
              return
            }
            e.preventDefault()
            e.stopPropagation()

            this.gridApi.setFocusedCell(
              TabStore.activeTab!.session!.script.recordIds.length - 1,
              this.gridApi.getFocusedCell().column
            )
          }}
        />
        <Mousetrap
          keys="mod+left"
          target={this.table}
          preventDefault={false}
          stopPropagation={false}
          onMatch={e => {
            if (this.gridApi.getEditingCells().length > 0) {
              return
            }
            e.preventDefault()
            e.stopPropagation()
            this.gridApi.setFocusedCell(
              this.gridApi.getFocusedCell().rowIndex,
              this.columnApi.getColumnState()[1].colId // Since 0 is the checkbox
            )
          }}
        />
        <Mousetrap
          keys="mod+right"
          target={this.table}
          preventDefault={false}
          stopPropagation={false}
          onMatch={e => {
            if (this.gridApi.getEditingCells().length > 0) {
              return
            }
            e.preventDefault()
            e.stopPropagation()

            const columnState = this.columnApi.getColumnState()
            this.gridApi.setFocusedCell(
              this.gridApi.getFocusedCell().rowIndex,
              columnState[columnState.length - 1].colId
            )
          }}
        />

        <Mousetrap
          keys="enter"
          target={this.table}
          preventDefault={false}
          stopPropagation={false}
          onMatch={e => {
            const focusedCell = this.gridApi.getFocusedCell()
            if (focusedCell.column.getColId() === 'checkbox') {
              e.preventDefault()
              e.stopPropagation()

              const rowNode = this.gridApi.getRowNode(
                TabStore.activeTab!.session!.script.recordIds[
                  focusedCell.rowIndex
                ]
              )

              const isSelected = this.gridApi
                .getSelectedNodes()
                .find(n => n.id === rowNode.id)
              rowNode?.setSelected(!isSelected)
            }
          }}
        />
      </>
    )
  }
}

export default Table
